import './index.scss';

import { useQuery } from '@tanstack/react-query';
import { Empty, Skeleton } from 'antd';
import { featuresAndInnovationsQueries } from '@/_api/features_and_innovations/query';
import { LiveFeaturesResponse } from '@/_api/features_and_innovations/type';
import { CButton } from '@/components/CButton';
import { HeroModal } from '@/components/HeroModal';
import { StatusBadge, StatusBadgeVariant } from '@/components/StatusBadge';

interface NewFeaturesModalProps {
  isUpdatesModalVisible: boolean;
  handleCloseUpdatesModal: () => void;
  t: (key: string) => string;
}

const STATUS_VARIANTS: Record<number, StatusBadgeVariant> = {
  0: 'accent',
  1: 'brand',
  2: 'neutral',
};

export const NewFeaturesModal = ({
  isUpdatesModalVisible,
  handleCloseUpdatesModal,
  t,
}: NewFeaturesModalProps) => {
  const { data: liveFeaturesData, isLoading: liveFeaturesLoading } = useQuery<LiveFeaturesResponse>(
    {
      queryKey: featuresAndInnovationsQueries.keys.liveFeatures(),
      queryFn: () => featuresAndInnovationsQueries.fn.fetchLiveFeatures(),
      enabled: isUpdatesModalVisible,
    }
  );
  const features = liveFeaturesData?.body ?? [];

  return (
    <HeroModal
      open={isUpdatesModalVisible}
      size="lg"
      badge="V2.14 / 21.10.2026"
      title={t('sidebar-menu.updatesModal.title')}
      description={t('sidebar-menu.updatesModal.intro')}
      footer={
        <div className="c-updates__footer">
          <span className="c-updates__footnote">{t('sidebar-menu.updatesModal.footer')}</span>
          <CButton variant="primary" size="sm" onClick={handleCloseUpdatesModal}>
            {t('sidebar-menu.updatesModal.gotIt')}
          </CButton>
        </div>
      }
      onClose={handleCloseUpdatesModal}
    >
      <div className="c-updates">
        {liveFeaturesLoading ? (
          [1, 2, 3].map((index) => (
            <Skeleton key={index} active paragraph={{ rows: 1, width: '80%' }} title={false} />
          ))
        ) : features.length > 0 ? (
          <ul className="c-updates__list">
            {features.map((item) => (
              <li key={item.id} className="c-updates__item">
                <StatusBadge
                  label={item.status?.name || t('sidebar-menu.updatesModal.defaultStatus')}
                  status={STATUS_VARIANTS[item.status?.id ?? 0] ?? 'neutral'}
                />
                <span className="c-updates__text">{item.title}</span>
              </li>
            ))}
          </ul>
        ) : (
          <Empty
            description={t('sidebar-menu.updatesModal.emptyState')}
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        )}
      </div>
    </HeroModal>
  );
};
